/* eslint-disable @typescript-eslint/no-unused-vars, no-empty-pattern, @typescript-eslint/no-empty-object-type, @typescript-eslint/no-explicit-any */
import { BaseQueryFn, createApi, EndpointBuilder, FetchArgs, fetchBaseQuery, FetchBaseQueryError, FetchBaseQueryMeta } from "@reduxjs/toolkit/query/react";
import Config from "../config";
import {
  AttendanceLogEntry,
  Child,
  Communication,
  CommunicationRecipient,
  CommunicationSendRecipient,
  DailyInspection,
  DailyReport,
  Family,
  Receipt,
  Recipient,
  ServicePeriod,
  SMSLog,
  SmsPrefixResponse,
  SmsSendRequest,
  SmsSendResponse,
} from "../models";
import { toQueryString } from "../utils/uri";
import { RootState } from "./store";

type Builder = EndpointBuilder<BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>, string, "api">;

export const Tags = {
  Child: "Child",
  Family: "Family",
  SMSLog: "SMSLog",
  Communication: "Communication",
  CommunicationRecipient: "CommunicationRecipient",
  DailyInspection: "DailyInspection",
  DailyReport: "DailyReport",
  AttendanceLogEntry: "AttendanceLogEntry",
  Receipt: "Receipt",
  ServicePeriod: "ServicePeriod",
};

const childrenEndpoints = (builder: Builder) => {
  return {
    getAllChildrenFiltered: builder.query<Child[], { familyId?: number; showInactive?: boolean }>({
      query: ({ familyId, showInactive }) => `children/filtered?${toQueryString({ familyId, showInactive })}`,
      providesTags: (result, error, arg) => [Tags.Child],
    }),
    getChildById: builder.query<Child, { id?: number }>({
      query: ({ id }) => `children/${id}`,
      providesTags: (result, error, arg) => [{ type: Tags.Child, id: arg.id }],
    }),
    saveChild: builder.mutation<Child, Child>({
      query: (child) => ({
        url: "children",
        method: "POST",
        body: child,
      }),
      invalidatesTags: (result, error, arg) => [Tags.Child, Tags.ServicePeriod],
    }),
    deleteChild: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `children/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [Tags.Child, Tags.ServicePeriod],
    }),
  };
};

const familiesEndpoints = (builder: Builder) => {
  return {
    getAllFamilies: builder.query<Family[], {}>({
      query: ({}) => `families`,
      providesTags: (result, error, arg) => [Tags.Family],
    }),
    getFamilyById: builder.query<Family, { id?: number }>({
      query: ({ id }) => `families/${id}`,
      providesTags: (result, error, arg) => [{ type: Tags.Family, id: arg.id }],
    }),
    saveFamily: builder.mutation<Family, Family>({
      query: (family) => ({
        url: "families",
        method: "POST",
        body: family,
      }),
      invalidatesTags: (result, error, arg) => [Tags.Family, Tags.ServicePeriod],
    }),
    deleteFamily: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `families/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [Tags.Family, Tags.ServicePeriod],
    }),
  };
};

const communicationRecipientsEndpoints = (builder: Builder) => {
  return {
    getAllCommunicationRecipients: builder.query<CommunicationRecipient[], {}>({
      query: ({}) => `communicationrecipients`,
      providesTags: (result, error, arg) => [Tags.CommunicationRecipient],
    }),
    getCommunicationRecipientById: builder.query<CommunicationRecipient, { id?: number }>({
      query: ({ id }) => `communicationrecipients/${id}`,
      providesTags: (result, error, arg) => [{ type: Tags.CommunicationRecipient, id: arg.id }],
    }),
    getCommunicationRecipientsFiltered: builder.query<Child[], { familyId?: number; phone?: string }>({
      query: ({ familyId, phone }) => `communicationrecipients/filtered?${toQueryString({ familyId, phone })}`,
      providesTags: (result, error, arg) => [Tags.CommunicationRecipient],
    }),
    saveCommunicationRecipient: builder.mutation<CommunicationRecipient, CommunicationRecipient>({
      query: (record) => ({
        url: "communicationrecipients",
        method: "POST",
        body: record,
      }),
      invalidatesTags: (result, error, arg) => [Tags.CommunicationRecipient, { type: Tags.Family, id: arg.familyId }],
    }),
    deleteCommunicationRecipient: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `communicationrecipients/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [Tags.CommunicationRecipient, Tags.Family],
    }),
  };
};

const communicationEndpoints = (builder: Builder) => {
  return {
    getCommunicationsFiltered: builder.query<Communication[], { childId?: number; rangeStartCreateDate?: string; rangeEndCreateDate?: string }>({
      query: ({ childId, rangeStartCreateDate, rangeEndCreateDate }) =>
        `communication/filtered?${toQueryString({ childId, rangeStartCreateDate, rangeEndCreateDate })}`,
      providesTags: (result, error, arg) => [Tags.Communication],
    }),
    getCommunicationById: builder.query<Communication, { id?: number }>({
      query: ({ id }) => `communication/${id}`,
      providesTags: (result, error, arg) => [{ type: Tags.Communication, id: arg.id }],
    }),
    saveCommunication: builder.mutation<Communication, Communication>({
      query: (record) => ({
        url: "communication",
        method: "POST",
        body: record,
      }),
      invalidatesTags: (result, error, arg) => [Tags.Communication],
    }),
    deleteCommunication: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `communication/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [Tags.Communication],
    }),
    getCommunicationSmsPrefix: builder.query<SmsPrefixResponse, {}>({
      query: ({}) => "communication/prefix",
    }),
    getCommunicationSendRecipients: builder.query<CommunicationSendRecipient[], { childId?: number }>({
      query: ({ childId }) => `communication/recipients?${toQueryString({ childId })}`,
      providesTags: (result, error, arg) => [Tags.CommunicationRecipient],
    }),
    sendCommunication: builder.mutation<SmsSendResponse, SmsSendRequest>({
      query: (request) => ({
        url: "communication/send",
        method: "POST",
        body: request,
      }),
      invalidatesTags: (result, error, arg) => [Tags.Communication, Tags.SMSLog, Tags.CommunicationRecipient],
    }),
    retryCommunicationLog: builder.mutation<Communication, { communicationId: number; logId: number }>({
      query: ({ communicationId, logId }) => ({
        url: `communication/${communicationId}/retry/${logId}`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [Tags.Communication, Tags.SMSLog, Tags.CommunicationRecipient],
    }),
  };
};

const smsLogEndpoints = (builder: Builder) => {
  return {
    getSMSLogsFiltered: builder.query<Child[], { rangeStartDate?: number; rangeEndDate?: number; phoneNumber?: string }>({
      query: ({ rangeStartDate, rangeEndDate, phoneNumber }) => `smslog/filtered?${toQueryString({ rangeStartDate, rangeEndDate, phoneNumber })}`,
      providesTags: (result, error, arg) => [Tags.SMSLog],
    }),
    getSMSLogById: builder.query<SMSLog, { id?: number }>({
      query: ({ id }) => `smslog/${id}`,
      providesTags: (result, error, arg) => [{ type: Tags.SMSLog, id: arg.id }],
    }),
    saveSMSLog: builder.mutation<SMSLog, SMSLog>({
      query: (record) => ({
        url: "smslog",
        method: "POST",
        body: record,
      }),
      invalidatesTags: (result, error, arg) => [Tags.SMSLog],
    }),
    deleteSMSLog: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `smslog/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [Tags.SMSLog],
    }),
  };
};

const dailyInspectionEndpoints = (builder: Builder) => {
  return {
    getDailyInspectionsFiltered: builder.query<
      DailyInspection[],
      { inspectionDate?: string; rangeStartInspectionDate?: string; rangeEndInspectionDate?: string }
    >({
      query: ({ inspectionDate, rangeStartInspectionDate, rangeEndInspectionDate }) =>
        `dailyinspections/filtered?${toQueryString({ inspectionDate, rangeStartInspectionDate, rangeEndInspectionDate })}`,
      providesTags: (result, error, arg) => [Tags.DailyInspection],
    }),
    getDailyInspectionById: builder.query<DailyInspection, { id?: number }>({
      query: ({ id }) => `dailyinspections/${id}`,
      providesTags: (result, error, arg) => [{ type: Tags.DailyInspection, id: arg.id }],
    }),
    saveDailyInspection: builder.mutation<DailyInspection, DailyInspection>({
      query: (record) => ({
        url: "dailyinspections",
        method: "POST",
        body: record,
      }),
      invalidatesTags: (result, error, arg) => [Tags.DailyInspection],
    }),
    deleteDailyInspection: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `dailyinspections/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [Tags.DailyInspection],
    }),
  };
};

const dailyReportsEndpoints = (builder: Builder) => {
  return {
    getDailyReportsFiltered: builder.query<DailyReport[], { rangeStartDate?: string; rangeEndDate?: string }>({
      query: ({ rangeStartDate, rangeEndDate }) => `dailyreports/filtered?${toQueryString({ rangeStartDate, rangeEndDate })}`,
      providesTags: (result, error, arg) => [Tags.DailyReport],
    }),
    getDailyReportById: builder.query<DailyReport, { id?: number }>({
      query: ({ id }) => `dailyinspections/${id}`,
      providesTags: (result, error, arg) => [{ type: Tags.DailyReport, id: arg.id }],
    }),
    saveDailyReport: builder.mutation<DailyReport, DailyReport>({
      query: (record) => ({
        url: "dailyreports",
        method: "POST",
        body: record,
      }),
      invalidatesTags: (result, error, arg) => [Tags.DailyReport],
    }),
    deleteDailyReport: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `dailyreports/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [Tags.DailyReport],
    }),
  };
};

const receiptsEndpoints = (builder: Builder) => {
  return {
    getAllReceiptsFiltered: builder.query<Receipt[], { familyId?: number; rangeStartCreateDate?: string; rangeEndCreateDate?: string; serviceDate?: string }>({
      query: ({ familyId, rangeStartCreateDate, rangeEndCreateDate, serviceDate }) =>
        `receipts/filtered?${toQueryString({ familyId, rangeStartCreateDate, rangeEndCreateDate, serviceDate })}`,
      providesTags: (result, error, arg) => [Tags.Receipt],
    }),
    getReceiptById: builder.query<Receipt, { id?: number }>({
      query: ({ id }) => `receipts/${id}`,
      providesTags: (result, error, arg) => [{ type: Tags.Receipt, id: arg.id }],
    }),
    getReceiptHtmlById: builder.query<string, { id?: number }>({
      query: ({ id }) => ({
        url: `receipts/${id}/html`,
        responseHandler: (response) => response.text(),
      }),
      providesTags: (result, error, arg) => [{ type: Tags.Receipt, id: arg.id }],
    }),
    getReceiptRecipientsById: builder.query<Recipient[], { id?: number }>({
      query: ({ id }) => `receipts/${id}/recipients`,
      providesTags: (result, error, arg) => [{ type: Tags.Receipt, id: arg.id }],
    }),
    getLatestReceipt: builder.query<Receipt, { familyId?: number; childId?: number; take?: number }>({
      query: ({ familyId, childId, take }) => `receipts/latest?${toQueryString({ familyId, childId, take })}`,
      providesTags: (result, error, arg) => [Tags.Receipt],
    }),
    saveReceipt: builder.mutation<Receipt, Receipt>({
      query: (item) => ({
        url: "receipts",
        method: "POST",
        body: item,
      }),
      invalidatesTags: (result, error, arg) => [Tags.Receipt],
    }),
    deleteReceipt: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `receipts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [Tags.Receipt],
    }),
    sendReceipt: builder.mutation<{ success: boolean; results: any[] }, { id: number; recipientEmails: string[] }>({
      query: ({ id, recipientEmails }) => ({
        url: `receipts/${id}/send`,
        method: "POST",
        body: { receiptId: id, recipientEmails },
      }),
    }),
  };
};

const attendanceLogsEndpoints = (builder: Builder) => {
  return {
    getLooseEnds: builder.query<AttendanceLogEntry[], {}>({
      query: ({}) => `attendanceLogs/looseends?${toQueryString({})}`,
      providesTags: (result, error, arg) => [Tags.AttendanceLogEntry],
    }),
    getAllAttendanceLogEntriesFiltered: builder.query<AttendanceLogEntry[], { childId?: number; rangeStartDate?: string; rangeEndDate?: string }>({
      query: ({ childId, rangeStartDate, rangeEndDate }) => `attendanceLogs/filtered?${toQueryString({ childId, rangeStartDate, rangeEndDate })}`,
      providesTags: (result, error, arg) => [Tags.AttendanceLogEntry],
    }),
    getAttendanceLogEntryById: builder.query<AttendanceLogEntry, { id?: number }>({
      query: ({ id }) => `attendanceLogs/${id}`,
      providesTags: (result, error, arg) => [{ type: Tags.AttendanceLogEntry, id: arg.id }],
    }),
    saveAttendanceLogEntry: builder.mutation<AttendanceLogEntry, AttendanceLogEntry>({
      query: (item) => ({
        url: "attendanceLogs",
        method: "POST",
        body: item,
      }),
      invalidatesTags: (result, error, arg) => [Tags.AttendanceLogEntry],
    }),
    deleteAttendanceLogEntry: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `attendanceLogs/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [Tags.AttendanceLogEntry],
    }),
  };
};

const servicePeriodsEndpoints = (builder: Builder) => {
  return {
    getAllServicePeriods: builder.query<ServicePeriod[], {}>({
      query: ({}) => `serviceperiods`,
      providesTags: (result, error, arg) => [Tags.ServicePeriod],
    }),
    getServicePeriodsFiltered: builder.query<ServicePeriod[], { familyId?: number; rangeStart?: string; rangeEnd?: string; paid?: boolean }>({
      query: ({ familyId, rangeStart, rangeEnd, paid }) => `serviceperiods/filtered?${toQueryString({ familyId, rangeStart, rangeEnd, paid })}`,
      providesTags: (result, error, arg) => [Tags.ServicePeriod],
    }),
    getServicePeriodById: builder.query<ServicePeriod, { id?: number }>({
      query: ({ id }) => `serviceperiods/${id}`,
      providesTags: (result, error, arg) => [{ type: Tags.ServicePeriod, id: arg.id }],
    }),
    saveServicePeriod: builder.mutation<ServicePeriod, ServicePeriod>({
      query: (item) => ({
        url: "serviceperiods",
        method: "POST",
        body: item,
      }),
      invalidatesTags: (result, error, arg) => [Tags.ServicePeriod],
    }),
    deleteServicePeriod: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `serviceperiods/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [Tags.ServicePeriod],
    }),
  };
};

const reportsEndpoints = (builder: Builder) => {
  return {
    getTaxReportHtml: builder.query<string, { familyId?: number; year?: number; lf?: string; yearTotal?: number }>({
      query: ({ familyId, year, lf, yearTotal }) => ({
        url: `reports/tax/html?${toQueryString({ familyId, year, lf, yearTotal })}`,
        responseHandler: (response) => response.text(),
      }),
    }),
    getInspectionReportHtml: builder.query<string, { month?: number; year?: number }>({
      query: ({ month, year }) => ({
        url: `reports/inspection/html?${toQueryString({ month, year })}`,
        responseHandler: (response) => response.text(),
      }),
    }),
  };
};

// Define a service using a base URL and expected endpoints
export const api = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: Config.api.uri,
    prepareHeaders: (headers, { getState }) => {
      // Get the token from the Redux state
      const accessToken = (getState() as RootState).auth.authenticate.result?.accessToken;

      if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
        headers.set("X-Auth-Scheme", "Session");
      }

      return headers;
    },
  }),
  tagTypes: Object.values(Tags),
  endpoints: (builder) => ({
    ...childrenEndpoints(builder),
    ...familiesEndpoints(builder),
    ...communicationRecipientsEndpoints(builder),
    ...communicationEndpoints(builder),
    ...smsLogEndpoints(builder),
    ...dailyInspectionEndpoints(builder),
    ...dailyReportsEndpoints(builder),
    ...attendanceLogsEndpoints(builder),
    ...receiptsEndpoints(builder),
    ...servicePeriodsEndpoints(builder),
    ...reportsEndpoints(builder),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAllAttendanceLogEntriesFilteredQuery,
  useGetAllChildrenFilteredQuery,
  useGetAllCommunicationRecipientsQuery,
  useGetAllFamiliesQuery,
  useGetAllReceiptsFilteredQuery,
  useGetAttendanceLogEntryByIdQuery,
  useGetChildByIdQuery,
  useGetCommunicationByIdQuery,
  useGetCommunicationSmsPrefixQuery,
  useGetCommunicationSendRecipientsQuery,
  useGetCommunicationRecipientByIdQuery,
  useGetCommunicationRecipientsFilteredQuery,
  useGetCommunicationsFilteredQuery,
  useGetDailyInspectionByIdQuery,
  useGetDailyInspectionsFilteredQuery,
  useGetDailyReportByIdQuery,
  useGetDailyReportsFilteredQuery,
  useGetFamilyByIdQuery,
  useGetLatestReceiptQuery,
  useGetReceiptHtmlByIdQuery,
  useGetReceiptRecipientsByIdQuery,
  useGetLooseEndsQuery,
  useGetReceiptByIdQuery,
  useGetSMSLogByIdQuery,
  useGetAllServicePeriodsQuery,
  useGetServicePeriodByIdQuery,
  useGetServicePeriodsFilteredQuery,
  useGetTaxReportHtmlQuery,
  useGetInspectionReportHtmlQuery,
  useGetSMSLogsFilteredQuery,
  useDeleteAttendanceLogEntryMutation,
  useDeleteChildMutation,
  useDeleteCommunicationMutation,
  useDeleteCommunicationRecipientMutation,
  useDeleteDailyInspectionMutation,
  useDeleteDailyReportMutation,
  useDeleteFamilyMutation,
  useDeleteReceiptMutation,
  useSendReceiptMutation,
  useDeleteServicePeriodMutation,
  useDeleteSMSLogMutation,
  useSaveAttendanceLogEntryMutation,
  useSaveChildMutation,
  useSendCommunicationMutation,
  useSaveDailyInspectionMutation,
  useSaveCommunicationRecipientMutation,
  useSaveDailyReportMutation,
  useSaveFamilyMutation,
  useSaveReceiptMutation,
  useSaveServicePeriodMutation,
  useSaveSMSLogMutation,
  useRetryCommunicationLogMutation,
} = api;
